import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import EmployeeDashboard from './pages/dashboard/EmployeeDashboard';
import SupervisorDashboard from './pages/dashboard/SupervisorDashboard';
import DirectorDashboard from './pages/dashboard/DirectorDashboard';
import MyLeavePage from './pages/leave/MyLeavePage';
import LeaveApplicationPage from './pages/leave/LeaveApplicationPage';
import LeaveDetailPage from './pages/leave/LeaveDetailPage';
import MyBalancesPage from './pages/balances/MyBalancesPage';
import CalendarPage from './pages/calendar/CalendarPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import HolidayManagementPage from './pages/admin/HolidayManagementPage';
import PayslipManagementPage from './pages/admin/PayslipManagementPage';
import ProfilePage from './pages/profile/ProfilePage';
import MyPayslipsPage from './pages/payslips/MyPayslipsPage';
import HelpPage from './pages/help/HelpPage';
import NotFoundPage from './pages/errors/NotFoundPage';
import LoadingSpinner from './components/common/LoadingSpinner';

function DashboardRouter() {
  const { user } = useAuth();

  if (!user) return <EmployeeDashboard />;

  switch (user.role) {
    case 'DIRECTOR':
    case 'ADMIN':
    case 'BOARD_APPROVER':
      return <DirectorDashboard />;
    case 'SUPERVISOR':
      return <SupervisorDashboard />;
    default:
      return <EmployeeDashboard />;
  }
}

function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardRouter />} />

        {/* Leave Routes */}
        <Route path="leave">
          <Route index element={<MyLeavePage />} />
          <Route path="apply" element={<LeaveApplicationPage />} />
          <Route path=":id" element={<LeaveDetailPage />} />
        </Route>

        {/* Balances */}
        <Route path="balances" element={<MyBalancesPage />} />

        {/* Payslips */}
        <Route path="payslips" element={<MyPayslipsPage />} />

        {/* Calendar */}
        <Route path="calendar" element={<CalendarPage />} />

        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />

        {/* Help */}
        <Route path="help" element={<HelpPage />} />

        {/* Reports */}
        <Route path="reports">
          <Route index element={<div className="card p-6">Reports - Coming Soon</div>} />
          <Route path="team" element={<div className="card p-6">Team Reports - Coming Soon</div>} />
        </Route>

        {/* Admin Routes */}
        <Route path="admin">
          <Route
            path="users"
            element={
              <ProtectedRoute roles={['ADMIN', 'DIRECTOR']}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <div className="card p-6">System Settings - Coming Soon</div>
              </ProtectedRoute>
            }
          />
          <Route
            path="holidays"
            element={
              <ProtectedRoute roles={['ADMIN', 'DIRECTOR']}>
                <HolidayManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="payslips"
            element={
              <ProtectedRoute roles={['ADMIN', 'DIRECTOR']}>
                <PayslipManagementPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Redirect /login to dashboard if already authenticated */}
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
